import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { apiGet } from "./client";

/**
 * GET /api/v2/ads/ozet — Reklam Performansı "Ortak Havuz" özeti (Meta +
 * Google Ads birleşik), backend/app/core/api/ads.py. Google ağı henüz
 * senkron edilmediği için `kaynaklar` içinde 0/`roas:null` olarak döner —
 * HATA sayılmaz, frontend "—" gösterir (bkz. OrtakHavuz.tsx).
 */

export const adsToplamSchema = z.object({
  spend: z.number(),
  conversion_value: z.number().nullable(),
  roas: z.number().nullable(),
  clicks: z.number(),
  impressions: z.number(),
  conversions: z.number().nullable(),
});
export type AdsToplam = z.infer<typeof adsToplamSchema>;

export const adsKaynakSchema = adsToplamSchema.extend({ network: z.string(), status: z.string().optional(), source: z.string().optional() });
export type AdsKaynak = z.infer<typeof adsKaynakSchema>;

export const adsGunlukSchema = z.object({
  day: z.string(),
  meta_spend: z.number(),
  google_spend: z.number(),
  toplam_spend: z.number(),
});
export type AdsGunluk = z.infer<typeof adsGunlukSchema>;

export const adsOncekiSchema = adsToplamSchema.extend({ start: z.string(), end: z.string() });
export type AdsOnceki = z.infer<typeof adsOncekiSchema>;

export const adsOzetResponseSchema = z
  .object({
    start: z.string(),
    end: z.string(),
    toplam: adsToplamSchema,
    kaynaklar: z.array(adsKaynakSchema),
    gunluk: z.array(adsGunlukSchema),
    onceki: adsOncekiSchema.nullable(),
  })
  .passthrough();
export type AdsOzetResponse = z.infer<typeof adsOzetResponseSchema>;

/** Legacy `vdd.kpi.adsRange` ile ayni sekil: 7/30/90 sekmesi YA DA ozel aralik. */
export type AdsRange = { gun: 7 | 30 | 90 } | { baslangic: string; bitis: string };

function rangeToQuery(range: AdsRange, karsilastir: boolean): string {
  const params = new URLSearchParams();
  if ("gun" in range) {
    params.set("days", String(range.gun));
  } else {
    params.set("start", range.baslangic);
    params.set("end", range.bitis);
  }
  if (karsilastir) params.set("karsilastir", "1");
  return params.toString();
}

export function fetchAdsOzet(range: AdsRange, karsilastir = false): Promise<AdsOzetResponse> {
  return apiGet(`/api/v2/ads/ozet?${rangeToQuery(range, karsilastir)}`, adsOzetResponseSchema);
}

export function adsOzetQueryKey(range: AdsRange, karsilastir: boolean) {
  return ["ads", "ozet", range, karsilastir] as const;
}

export function useAdsOzet(range: AdsRange, karsilastir = false) {
  return useQuery({
    queryKey: adsOzetQueryKey(range, karsilastir),
    queryFn: () => fetchAdsOzet(range, karsilastir),
    staleTime: 30_000,
  });
}
